import React, {useState} from "react";
import {Button, DialogContent, DialogTitle, Grid, TextField} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {api} from "../../../../api/API";
import {WoodDetailItem} from "../../../../api/Openings";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useFilePicker} from "../../shopdrawing/extras/FilePicker";
import {useAsyncAction} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import {ext} from "../../../../api/Files";
import {decorate} from "../../../../misc/pdf/parts/CoverPage";
import {RowOpeningChange} from "../freeformchanges/products/bulkChanges/DoorFrameDetails";

export function WoodDetailFilePopup<T extends (WoodDetailItem)>(props: {
    anchor: any;
    row: T;
    width: number;
    initialValue: string;
    onCancel(): void;
    onDone(value: T): Promise<any>;
}) {

    return <WoodDetailFileDialog
        row={props.row}
        onCancel={props.onCancel}
        onDone={(file) => {
            return props.onDone(Object.assign({}, props.row, {
                doorWdProofFile: file ? file.id : null,
                doorWdProofFileName: file ? file.name : null,
            }))
        }}
    />
}

export function WoodDetailFilePopupForFreeformChange<T extends (RowOpeningChange)>(props: {
    anchor: any;
    row: T;
    width: number;
    initialValue: string;
    onCancel(): void;
    onDone(value: T): Promise<any>;
}) {

    return <WoodDetailFileDialog
        row={{
            doorMaterial: "material",
            doorWdMaterialName: "",
            doorWdProofFile: props.row.value || null,
            doorWdProofFileName: props.row.value ? "file.pdf" : null,
        }}
        onCancel={props.onCancel}
        onDone={(file) => {
            return props.onDone(Object.assign({}, props.row, {
                value: file?.id || null,
            }))
        }}
    />
}

export function WoodDetailFileDialog(props: {
    row: {
        doorMaterial: string;
        doorWdMaterialName: string;
        doorWdProofFile?: number | null;
        doorWdProofFileName?: string | null;
    }
    onCancel(): void;
    onDone(file: {id: number; name: string} | null): Promise<any>
}) {

    const filePicker = useFilePicker({
        accept: ["application/pdf", "image/*"]
    });

    const [name] = useState(props.row.doorMaterial + (decorate(props.row.doorWdMaterialName, " - ") || ""));
    const [file, setFile] = useState(props.row.doorWdProofFile ? {name: props.row.doorWdProofFileName || "file.pdf", id: props.row.doorWdProofFile} : null);

    const onDoneRef = useSyncedRef(props.onDone);
    const doneCb = useAsyncAction(async (input) => {
        await onDoneRef.current(input)
    }, []);

    const upload = useAsyncAction(async (file: File) => {
        const fileObj = await api.files.upload({
            file: file,
            // use guid naming to prevent collisions
            name: window.crypto.randomUUID() + ext(file.name),
            convert2pdf: true,
        });

        setFile({
            id: fileObj.id,
            name: file.name,
        });
    }, [filePicker]);

    const size = useWindowSize();

    const submit = () => {
        doneCb.callback(file)
    };

    return (
        <Dialog2 open fullWidth maxWidth="lg" onClose={props.onCancel} onEnterPress={submit}>
            <DialogTitle>Wood Detail Proof</DialogTitle>
            <DialogContent>
                <div style={{height: 8}} />
                <Grid container spacing={1}>
                    <Grid item xs={8}>
                        {file ? <iframe
                            src={api.files.downloadLink(file.id, "inline")}
                            style={{
                                height: size.height * 0.75,
                                width: "100%",
                                border: "1px solid black",
                            }}
                        ></iframe> : <div>Choose a file on the right</div>}
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            value={name}
                            disabled
                            label="Name"
                            fullWidth
                            size="small"
                        />

                        <div style={{height: 24}} />

                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs>
                                {file ? file.name : "No file selected"}
                            </Grid>
                            <Grid item>
                                <Button variant="outlined" size="small" onClick={async () => {
                                    const files = await filePicker();
                                    upload.callback(files[0])
                                }}>
                                    Choose File
                                </Button>
                            </Grid>
                            {file && <Grid item onClick={() => setFile(null)}>
                                <Button variant="outlined" size="small">
                                    Clear File
                                </Button>
                            </Grid>}
                        </Grid>
                    </Grid>
                </Grid>

            </DialogContent>
            <SmoothActionBar
                loading={doneCb.loading}
                error={doneCb.error}
                onCancel={props.onCancel}
                onSubmit={submit}
                actionName="Done"
            />
        </Dialog2>
    )
}